import React, { useEffect } from 'react';
import { brandColors, styles } from '../styles/clientManagementStyles';
import { colors as theme } from '../theme';
import NeedsYouGroup from './NeedsYouGroup';

const ClientListView = ({
  onBack,
  clients,
  programs,
  loading,
  error,
  clientFlagCounts,
  clientUnreadMessageCounts,
  coaches,
  getClientPrograms,
  selectClient,
  hasMoreClients,
  loadingMoreClients,
  onLoadMoreClients,
  clientTotalCount,
  clientTab = 'active',
  onChangeClientTab,
  activeClientCount = 0,
  inactiveClientCount = 0,
  archivingClient,
  onArchiveClient,
  getInactiveReason,
  needs,
  onOpenNeedsClient,
}) => {
  const onInactiveTab = clientTab === 'inactive';
  // Back on the list (after reading messages or reviewing a check-in): refresh who needs you, quietly
  const reloadNeeds = needs?.reload;
  useEffect(() => { reloadNeeds?.({ quiet: true }); }, [reloadNeeds]);
  return (
    <div style={styles.container}>
      <button onClick={onBack} style={styles.backBtn}>
        ← Back to Admin Panel
      </button>

      <h1 style={styles.title}>Client Management</h1>
      <p style={styles.subtitle}>
        {hasMoreClients && clientTotalCount != null
          ? `Showing ${clients.length} of ${clientTotalCount} clients`
          : `${clients.length} ${clients.length === 1 ? 'client' : 'clients'}`}
        {' • '}{programs.filter(p => p.user_id).length} active programs
      </p>

      {/* Active / Inactive tabs */}
      {onChangeClientTab && (
        <div style={{ display: 'flex', gap: '8px', margin: '4px 0 20px' }}>
          {[
            { key: 'active', label: 'Active', count: activeClientCount },
            { key: 'inactive', label: 'Inactive', count: inactiveClientCount },
          ].map(t => {
            const on = clientTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => onChangeClientTab(t.key)}
                style={{
                  padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  color: on ? brandColors.darkGray : brandColors.silver,
                  background: on ? brandColors.gold : 'transparent',
                  border: `1px solid ${on ? brandColors.gold : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {t.label} <span style={{ opacity: 0.75, fontWeight: 700 }}>{t.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Clients who need the coach come first (plan 004) */}
      {!onInactiveTab && <NeedsYouGroup needs={needs} onOpen={onOpenNeedsClient} />}

      {clients.length === 0 && !loading && (
        <p style={{ ...styles.subtitle, color: brandColors.silver, fontStyle: 'italic' }}>
          {onInactiveTab
            ? 'No inactive clients. Canceled, suspended and dormant clients will collect here.'
            : 'No clients assigned yet. New clients will appear here when they sign up with your coach code.'}
        </p>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {/* New client review banner */}
      {(() => {
        const reviewCount = clients.filter(c => c.needs_review).length;
        if (reviewCount === 0) return null;
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 18px',
            marginBottom: '20px',
            backgroundColor: 'rgba(212,175,55,0.1)',
            border: '1px solid rgba(212,175,55,0.35)',
            borderRadius: '8px',
          }}>
            <span style={{ fontSize: '18px' }}>🔔</span>
            <div style={{ flex: 1 }}>
              <span style={{ color: theme.gold, fontWeight: 700, fontSize: '14px' }}>
                {reviewCount} new {reviewCount === 1 ? 'client' : 'clients'} need{reviewCount === 1 ? 's' : ''} program review
              </span>
              <p style={{ color: theme.textMuted, margin: '2px 0 0', fontSize: '12px' }}>
                AI auto-assigned programs — click a client marked NEW to review and approve.
              </p>
            </div>
          </div>
        );
      })()}

      <div style={{ ...styles.clientsGrid, gap: '10px' }}>
        {clients.map(client => {
          const clientPrograms = getClientPrograms(client.user_id);
          const unread = clientUnreadMessageCounts[client.user_id] || 0;
          const flags = clientFlagCounts[client.user_id] || 0;
          const inactiveReason = client.archived_at || onInactiveTab
            ? (getInactiveReason ? getInactiveReason(client) : 'Inactive')
            : null;
          const badge = (bg, fg) => ({
            backgroundColor: bg, color: fg, borderRadius: '8px', padding: '1px 7px',
            fontSize: '11px', fontWeight: 700, lineHeight: '18px', flexShrink: 0,
          });
          // 15 October 2026 (Codex's premium review, 5): one compact card per client. Name and the
          // badges on one line, the program and goal on the next; the email is in the tooltip and on
          // the client's own screen. Archive stays apart from the card's tap.
          return (
            <div
              key={client.id}
              role="button"
              tabIndex={0}
              title={client.email || undefined}
              style={{ ...styles.clientCard, padding: '12px 14px' }}
              onClick={() => selectClient(client)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectClient(client); } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ ...styles.smallAvatar, width: '36px', height: '36px', fontSize: '16px' }} aria-hidden="true">
                  {client.full_name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <span style={{ color: brandColors.white, fontSize: '15px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {client.full_name}
                    </span>
                    {client.needs_review && <span style={badge(theme.gold, theme.surface1)}>NEW</span>}
                    {unread > 0 && <span style={badge(brandColors.gold + '30', brandColors.gold)} title="Unread messages">💬 {unread}</span>}
                    {flags > 0 && <span style={badge(theme.danger + '30', theme.danger)} title="Flagged exercises">🚩 {flags}</span>}
                  </div>
                  <div style={{ marginTop: '3px', fontSize: '12px', color: brandColors.silver, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {clientPrograms.length > 0
                      ? <span style={{ color: brandColors.gold }}>{clientPrograms[0].name}</span>
                      : <span style={{ fontStyle: 'italic' }}>No program</span>}
                    {client.primary_goal && <> · {client.primary_goal.replace(/_/g, ' ')}</>}
                    {client.age && <> · {client.age} yrs</>}
                    {client.has_injuries && <span style={{ color: theme.warning }}> · injuries</span>}
                    {client.coach_id && coaches[client.coach_id] && <> · coach {coaches[client.coach_id]}</>}
                  </div>
                  {inactiveReason && (
                    <div style={{ marginTop: '2px', fontSize: '11px', color: brandColors.silver, fontStyle: 'italic' }}>{inactiveReason}</div>
                  )}
                </div>
                {onArchiveClient && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchiveClient(client, !client.archived_at);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    disabled={archivingClient === client.user_id}
                    style={{
                      padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                      fontFamily: 'inherit', flexShrink: 0,
                      cursor: archivingClient === client.user_id ? 'not-allowed' : 'pointer',
                      background: 'transparent',
                      border: `1px solid ${client.archived_at ? brandColors.gold : 'rgba(255,255,255,0.14)'}`,
                      color: client.archived_at ? brandColors.gold : brandColors.silver,
                    }}
                  >
                    {archivingClient === client.user_id
                      ? '…'
                      : client.archived_at ? 'Restore' : 'Archive'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hasMoreClients && (
        <button
          onClick={onLoadMoreClients}
          disabled={loadingMoreClients}
          style={{
            display: 'block',
            margin: '20px auto 0',
            padding: '12px 28px',
            background: 'transparent',
            border: `1px solid ${brandColors.gold}`,
            color: brandColors.gold,
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loadingMoreClients ? 'default' : 'pointer',
            opacity: loadingMoreClients ? 0.6 : 1,
          }}
        >
          {loadingMoreClients ? 'Loading…' : 'Load More Clients'}
        </button>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ClientListView;
